<template>
    <div class="rd">
        <div class="pagina">
            <header class="paginakop">
                <div class="tekst">
                    <h1>X-Cube Control</h1>
                    <p>Restart the host application on an X-Cube from a distance.</p>
                </div>
            </header>

            <div class="melding-blok letop" role="note">
                <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M12 7v6M12 17h.01" /></svg>
                <div>
                    <b>Only use this with permission from X-Corp.</b> A restart ends a game that is
                    running. Use it when the main screen does not respond to touch, or when a game
                    was started in the wrong language. The X-Cube needs a working internet connection.
                </div>
            </div>

            <p v-if="loading" class="rd-laden">Loading list of available X-Cubes...</p>

            <section v-else class="paneel">
                <div style="max-width: 460px">
                    <div class="veld">
                        <label for="c-cube">X-Cube</label>
                        <select id="c-cube" v-model="selectedItem" @change="confirming = false">
                            <option :value="null">Select an X-Cube</option>
                            <option v-for="x in displayItems" :key="x.id" :value="x">{{ x.display }}</option>
                        </select>
                    </div>

                    <div v-if="selectedItem" style="margin-top: 20px">
                        <div v-if="confirming" class="bevestig">
                            <span>
                                <b>Make sure no game is in progress.</b> Restart the host app on
                                <b>{{ selectedItem.name }}</b>? Any running game stops.
                            </span>
                            <div class="knoprij">
                                <button class="btn btn-rood" type="button" :disabled="restarting" @click="handleConfirmRestart">Yes, restart {{ selectedItem.name }}</button>
                                <button class="btn btn-stil" type="button" @click="confirming = false">Cancel</button>
                            </div>
                        </div>
                        <template v-else>
                            <p style="margin: 0 0 12px">
                                Selected X-Cube: <b>{{ selectedItem.name }}</b> — {{ selectedItem.location?.name }}
                            </p>
                            <button class="btn btn-paars" type="button" @click="confirming = true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v8" /><path d="M18 7a8 8 0 1 1-12 0" /></svg>
                                Restart host {{ selectedItem.name }}
                            </button>
                        </template>
                    </div>
                </div>
            </section>

            <section class="paneel">
                <div class="paneel-kop"><h2>Full control panel</h2></div>
                <p class="muted support-uitleg">
                    This page only restarts the host app. For everything else, use
                    <span class="num">control.x-cube.nl</span>: live status per X-Cube, triage when
                    something breaks, control of lights, screens and sound, and restarting an X-Cube
                    or its game software. Sign in with the same email address you use for the Hub.
                </p>
                <div class="knoprij">
                    <a class="btn btn-paars" href="https://control.x-cube.nl" target="_blank" rel="noopener">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>
                        Open control.x-cube.nl
                    </a>
                </div>
            </section>
        </div>
    </div>
</template>

<script setup>
import { Message } from "element-ui";
import { computed, onMounted, ref } from "vue";
import { getUserLocationsSimple } from "../../api/graphql/locations";
import { updateXCube } from "../../api/rest";

const items = ref([]);
const selectedItem = ref(null);
const loading = ref(false);
const confirming = ref(false);
const restarting = ref(false);

const displayItems = computed(() =>
    items.value
        .map((x) => ({ ...x, display: `${x.location?.name || "????????"} · ${x.name}${x.letter ? ` ${x.letter}` : ""}` }))
        .sort((a, b) => (a.location?.name || "").localeCompare(b.location?.name || "") || a.name.localeCompare(b.name)),
);

const fetchItems = async () => {
    loading.value = true;
    try {
        const locations = await getUserLocationsSimple();
        items.value = locations.reduce((acc, loc) => acc.concat(loc.xcubes.map((x) => ({ ...x, location: loc }))), []);
    } catch {
        Message.error("Failed to load X-Cubes");
    } finally {
        loading.value = false;
    }
};

const handleConfirmRestart = async () => {
    restarting.value = true;
    try {
        await updateXCube(selectedItem.value.id, { request_restart: true });
        Message.success(`Restart requested for ${selectedItem.value.name}. The host app is back in about a minute.`);
        confirming.value = false;
    } catch (error) {
        Message.error(`Restart failed: ${error.message}`);
    } finally {
        restarting.value = false;
    }
};

onMounted(fetchItems);
</script>
